import { useEffect, useRef, useState, useCallback } from 'react';

export function useWebSocket(token) {
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const shouldReconnect = useRef(true);
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);

  const connect = useCallback(() => {
    if (!token) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace(/^http/, 'ws') + `/api/ws?token=${token}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type !== 'pong') {
          setLastMessage(data);
        }
      } catch (e) {
        console.error('WS parse error:', e);
      }
    };

    ws.onclose = (event) => {
      setConnected(false);
      if (event.code === 4001 || !shouldReconnect.current) return;
      // Faster reconnect on mobile (2 seconds)
      reconnectRef.current = setTimeout(connect, 2000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [token]);

  useEffect(() => {
    shouldReconnect.current = true;
    connect();
    return () => {
      shouldReconnect.current = false;
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [connect]);

  // Ping interval to keep connection alive
  useEffect(() => {
    if (!connected) return;
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [connected]);

  return { connected, lastMessage };
}
